import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Link as LinkIcon, Power, RefreshCw, Send, Trash2 } from 'lucide-react'
import {
	deletePayingAgent,
	fetchPayingAgentOnboardingById,
	finalizeOnboarding,
	generateBiometricLink,
	updatePayingAgent,
} from '../../services/api'
import Loader from '../../components/Loader'
import {
	Alert,
	Badge,
	Button,
	Card,
	CopyableId,
	CopyButton,
	DetailGrid,
	DetailItem,
	EmptyState,
	JsonViewer,
	PageHeader,
	formatDateTime,
	formatDocument,
	statusTone,
} from '../../components/ui'

const PayingAgentDetails = () => {
	const { onboardingId } = useParams()
	const [data, setData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isBusy, setIsBusy] = useState(false)
	const [generatingLinkFor, setGeneratingLinkFor] = useState(null)
	const [error, setError] = useState('')
	const [feedback, setFeedback] = useState('')
	const [showRaw, setShowRaw] = useState(false)

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		try {
			setData(await fetchPayingAgentOnboardingById(onboardingId))
		} catch (err) {
			setError(err.message)
			setData(null)
		} finally {
			setIsLoading(false)
		}
	}, [onboardingId])

	useEffect(() => {
		load()
	}, [load])

	const handleToggleActive = async () => {
		setIsBusy(true)
		setError('')
		setFeedback('')
		try {
			await updatePayingAgent(data.payingAgentId, !data.active)
			setFeedback(`Pagadoria ${data.active ? 'desativada' : 'ativada'} com sucesso`)
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsBusy(false)
		}
	}

	const handleDelete = async () => {
		if (!window.confirm(`Desativar a pagadoria ${data.payingAgentId}?`)) return

		setIsBusy(true)
		setError('')
		setFeedback('')
		try {
			await deletePayingAgent(data.payingAgentId)
			setFeedback('Pagadoria desativada')
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsBusy(false)
		}
	}

	const handleFinalize = async () => {
		const confirmado = window.confirm(
			'Finalizar o onboarding da pagadoria? Dependendo do status atual isso envia ' +
				'os dados para o BaaS e reserva a conta.'
		)
		if (!confirmado) return

		setIsBusy(true)
		setError('')
		setFeedback('')
		try {
			await finalizeOnboarding(onboardingId)
			setFeedback('Onboarding finalizado')
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsBusy(false)
		}
	}

	const handleGenerateBiometricLink = async (partner) => {
		setGeneratingLinkFor(partner.id)
		setError('')
		setFeedback('')
		try {
			await generateBiometricLink(onboardingId, partner.id, partner.legalRepresentative)
			setFeedback('Link de biometria gerado')
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setGeneratingLinkFor(null)
		}
	}

	if (isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader size="lg" />
			</div>
		)
	}

	const onboarding = data?.onboarding
	const partners = onboarding?.partners || []
	const hasPayingAgent = Boolean(data?.payingAgentId)

	return (
		<div>
			<Link
				to="/pagadoria"
				className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
			>
				<ArrowLeft className="mr-1 h-4 w-4" />
				Voltar para pagadoria
			</Link>

			<PageHeader
				title={onboarding?.legalName || 'Pagadoria'}
				subtitle={`GET /backoffice/paying-agents/onboardings/${onboardingId}`}
				actions={
					<>
						<Button variant="secondary" onClick={() => setShowRaw((prev) => !prev)}>
							{showRaw ? 'Ocultar JSON' : 'Ver JSON'}
						</Button>
						<Button variant="secondary" onClick={load}>
							<RefreshCw className="h-4 w-4" />
							Recarregar
						</Button>
						{data &&
							(hasPayingAgent ? (
								<>
									<Button variant="secondary" disabled={isBusy} onClick={handleToggleActive}>
										<Power className="h-4 w-4" />
										{data.active ? 'Desativar' : 'Ativar'}
									</Button>
									<Button variant="danger" disabled={isBusy} onClick={handleDelete}>
										<Trash2 className="h-4 w-4" />
										Excluir
									</Button>
								</>
							) : (
								<Button disabled={isBusy} onClick={handleFinalize}>
									<Send className="h-4 w-4" />
									Finalizar onboarding
								</Button>
							))}
					</>
				}
			/>

			<Alert onDismiss={() => setError('')}>{error}</Alert>
			<Alert type="success" onDismiss={() => setFeedback('')}>
				{feedback}
			</Alert>

			{!data ? (
				<EmptyState message="Onboarding de pagadoria nao encontrado" />
			) : (
				<div className="space-y-6">
					<Card title="Dados do onboarding">
						<DetailGrid cols={2}>
							<DetailItem label="Onboarding">
								<CopyableId value={onboarding.id} />
							</DetailItem>
							<DetailItem label="Status">
								<Badge tone={statusTone(onboarding.status)}>{onboarding.status}</Badge>
							</DetailItem>
							<DetailItem label="Razao social">{onboarding.legalName}</DetailItem>
							<DetailItem label="CNPJ">{formatDocument(onboarding.document)}</DetailItem>
							<DetailItem label="Conta de comissao">
								<Link
									to={`/contas/${data.commissionAccountPublicId}`}
									className="text-primary-600 hover:underline"
								>
									{data.commissionAccountPublicId}
								</Link>
							</DetailItem>
							<DetailItem label="Solicitada em">{formatDateTime(onboarding.createdAt)}</DetailItem>
						</DetailGrid>
					</Card>

					{hasPayingAgent && (
						<Card title="Pagadoria">
							<DetailGrid cols={2}>
								<DetailItem label="Public ID">
									<CopyableId value={data.payingAgentId} />
								</DetailItem>
								<DetailItem label="Situacao">
									<Badge tone={data.active ? 'green' : 'red'}>
										{data.active ? 'Ativa' : 'Inativa'}
									</Badge>
								</DetailItem>
							</DetailGrid>
						</Card>
					)}

					<Card title={`Socios (${partners.length})`}>
						{!hasPayingAgent && (
							<div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
								Solicitacao em andamento. A conta de pagadoria so e criada depois que a
								biometria dos socios for refeita e o onboarding for finalizado.
							</div>
						)}
						{partners.length === 0 ? (
							<EmptyState message="Nenhum socio informado" />
						) : (
							<ul className="space-y-2 text-sm text-gray-700">
								{partners.map((partner) => (
									<li key={partner.id} className="flex flex-wrap items-center gap-2">
										<span>
											{partner.name} — {formatDocument(partner.document)}
										</span>
										{partner.legalRepresentative && (
											<Badge tone="blue">representante legal</Badge>
										)}
										{partner.biometric && (
											<Badge tone={statusTone(partner.biometric.status)}>
												{partner.biometric.status}
											</Badge>
										)}
										{(partner.biometric?.documents || []).map((doc) => (
											<Badge key={doc.id} tone={statusTone(doc.result)}>
												{doc.documentType}: {doc.result}
											</Badge>
										))}
										{partner.biometricLink ? (
											<CopyButton value={partner.biometricLink} label="Copiar link" />
										) : (
											<button
												type="button"
												onClick={() => handleGenerateBiometricLink(partner)}
												disabled={generatingLinkFor === partner.id}
												className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
											>
												<LinkIcon className="h-3.5 w-3.5" />
												{generatingLinkFor === partner.id ? 'Gerando...' : 'Gerar link'}
											</button>
										)}
									</li>
								))}
							</ul>
						)}
					</Card>

					{showRaw && (
						<Card title="Resposta bruta">
							<JsonViewer data={data} />
						</Card>
					)}
				</div>
			)}
		</div>
	)
}

export default PayingAgentDetails
